import React, { useState, useEffect } from 'react';
import { getProductStock, initializeFlashcardStock, FLASHCARD_PRODUCT_ID, updateProductStock, simulatePurchase } from '../../services/stockService';
import { Package, RefreshCw, Save, ShoppingCart } from 'lucide-react';
import toast from 'react-hot-toast';

const StockManager = () => {
  const [stockInfo, setStockInfo] = useState({
    id: '',
    name: '',
    initialStock: 0,
    currentStock: 0,
    isAvailable: false,
    lastUpdated: new Date()
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [newStock, setNewStock] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchStock();
  }, []);

  const fetchStock = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // S'assurer que le stock est initialisé
      await initializeFlashcardStock();
      
      // Récupérer les informations de stock
      const stock = await getProductStock(FLASHCARD_PRODUCT_ID);
      
      setStockInfo(stock);
      setNewStock(stock.currentStock);
    } catch (error) {
      console.error('Erreur lors de la récupération du stock:', error);
      setError('Erreur lors de la récupération du stock');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStock = async () => {
    try {
      setIsUpdating(true);
      setError('');
      setSuccess('');
      
      await updateProductStock(FLASHCARD_PRODUCT_ID, newStock);
      
      setSuccess('Stock mis à jour avec succès');
      fetchStock();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du stock:', error);
      setError('Erreur lors de la mise à jour du stock');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSimulatePurchase = async () => {
    try {
      setIsSimulating(true);
      setError('');
      setSuccess('');
      
      await simulatePurchase();
      
      setSuccess('Achat simulé avec succès');
      fetchStock();
    } catch (error) {
      console.error('Erreur lors de la simulation d\'achat:', error);
      setError('Erreur lors de la simulation d\'achat');
    } finally {
      setIsSimulating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#9a6ebd] border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-[#323c5f]">Gestion du stock</h2>
        <button
          onClick={fetchStock}
          className="flex items-center text-[#9a6ebd] hover:text-[#8a5dad] transition-colors"
        >
          <RefreshCw size={18} className="mr-1" />
          Actualiser
        </button>
      </div>
      
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-[#9a6ebd]/10 rounded-full flex items-center justify-center">
            <Package className="text-[#9a6ebd]" size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-[#323c5f]">{stockInfo.name}</h3>
            <p className="text-sm text-gray-600">
              Dernière mise à jour: {stockInfo.lastUpdated.toLocaleString()}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Stock initial</p>
            <p className="text-2xl font-bold text-[#323c5f]">{stockInfo.initialStock}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Stock actuel</p>
            <p className="text-2xl font-bold text-[#323c5f]">{stockInfo.currentStock}</p>
          </div>
        </div>
        
        <div className="mb-6">
          <label htmlFor="newStock" className="block text-sm font-medium text-gray-700 mb-2">
            Mettre à jour le stock
          </label>
          <div className="flex gap-4">
            <input
              type="number"
              id="newStock"
              min="0"
              value={newStock}
              onChange={(e) => setNewStock(parseInt(e.target.value) || 0)}
              className="flex-grow rounded-md border-gray-300 shadow-sm focus:border-[#9a6ebd] focus:ring focus:ring-[#9a6ebd] focus:ring-opacity-50"
            />
            <button
              onClick={handleUpdateStock}
              disabled={isUpdating}
              className="bg-[#9a6ebd] text-white px-4 py-2 rounded-md hover:bg-[#8a5dad] transition-colors flex items-center"
            >
              {isUpdating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Mise à jour...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mb-6">
          <button
            onClick={handleSimulatePurchase}
            disabled={isSimulating}
            className="w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors flex items-center justify-center"
          >
            {isSimulating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Simulation en cours...
              </>
            ) : (
              <>
                <ShoppingCart size={18} className="mr-2" />
                Simuler un achat (décrémenter le stock)
              </>
            )}
          </button>
          <p className="mt-2 text-xs text-gray-500 text-center">
            Ce bouton simule un achat validé sur Stripe (pour les tests uniquement)
          </p>
        </div>
        
        <div className="bg-yellow-50 p-4 rounded-lg mb-4">
          <p className="text-sm text-yellow-700">
            <strong>Note:</strong> Pour utiliser les fonctions de webhook Stripe, vous devez déployer l'application en production. 
            Les fonctions Cloud Firebase ne sont pas accessibles depuis l'environnement de développement local.
          </p>
        </div>
        
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-4">
            {success}
          </div>
        )}
        
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Le stock est automatiquement décrémenté à chaque achat validé sur Stripe.
            Utilisez cette interface uniquement pour des ajustements manuels.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StockManager;